import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartGroupDto } from '@data/_generated-api/platform-api/models/platform-chart-group-dto';
import { collectChartGroupSubtreeIds } from './hd-chart-group-tree.util';
import { HdIconPickerComponent } from './hd-icon-picker.component';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** null group = aanmaken (optioneel met voorgeselecteerde parent); anders bewerken. */
export interface HdChartGroupEditTarget {
  group: PlatformChartGroupDto | null;
  parentGroupId: string | null;
}

export interface HdChartGroupEditResult {
  name: string;
  parentGroupId: string | null;
  isDefaultSelection: boolean;
  /** Bare FA-icoonnaam; null = standaardicoon (folder-tree). */
  icon: string | null;
}

@Component({
  selector: 'hd-chart-group-edit-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule, NgSelectModule, HdIconPickerComponent],
  templateUrl: './hd-chart-group-edit-dialog.component.html',
  styleUrl: './hd-chart-group-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartGroupEditDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** null = gesloten. */
  readonly target = model<HdChartGroupEditTarget | null>(null);
  readonly groups = input<PlatformChartGroupDto[]>([]);
  readonly saved = output<HdChartGroupEditResult>();

  protected readonly name = signal('');
  protected readonly parentGroupId = signal<string | null>(null);
  protected readonly isDefaultSelection = signal(false);
  protected readonly icon = signal<string | null>(null);

  constructor() {
    effect(() => {
      const target = this.target();
      if (!target) return;
      this.name.set(target.group?.name ?? '');
      this.parentGroupId.set(target.group?.parentGroupId ?? target.parentGroupId ?? null);
      this.isDefaultSelection.set(target.group?.isDefaultSelection ?? false);
      this.icon.set(target.group?.icon ?? null);
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      createTitle: translate('humanDesign.chartManager.groups.newGroup'),
      editTitle: translate('humanDesign.chartManager.groups.editGroup'),
      nameLabel: translate('humanDesign.chartManager.groups.nameLabel'),
      parentLabel: translate('humanDesign.chartManager.groups.parentLabel'),
      noParent: translate('humanDesign.chartManager.groups.noParent'),
      iconLabel: translate('humanDesign.chartManager.groups.iconLabel'),
      defaultSelectionLabel: translate('humanDesign.chartManager.groups.defaultSelectionLabel'),
      save: translate('humanDesign.chartManager.groups.save'),
      cancel: translate('humanDesign.chartManager.groups.cancel'),
    };
  });

  protected readonly title = computed(() =>
    this.target()?.group ? this.text().editTitle : this.text().createTitle,
  );

  /**
   * Parent-opties met inspring-prefix; bij bewerken vallen de groep zelf en zijn
   * hele subtree weg (server checkt dit ook, maar de UI biedt het niet eens aan).
   */
  protected readonly parentOptions = computed(() => {
    const groups = this.groups();
    const editingId = this.target()?.group?.id ?? null;
    const excluded = editingId ? collectChartGroupSubtreeIds(groups, editingId) : new Set<string>();

    const childrenByParent = new Map<string | null, PlatformChartGroupDto[]>();
    for (const group of groups) {
      const key = group.parentGroupId ?? null;
      const siblings = childrenByParent.get(key) ?? [];
      siblings.push(group);
      childrenByParent.set(key, siblings);
    }

    const options: { value: string | null; label: string }[] = [
      { value: null, label: this.text().noParent },
    ];
    const visit = (parentId: string | null, level: number) => {
      for (const group of childrenByParent.get(parentId) ?? []) {
        if (!group.id || excluded.has(group.id)) continue;
        options.push({ value: group.id, label: `${'—'.repeat(level)}${level ? ' ' : ''}${group.name}` });
        visit(group.id, level + 1);
      }
    };
    visit(null, 0);
    return options;
  });

  protected get canSave(): boolean {
    return this.name().trim().length > 0;
  }

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected save(): void {
    const name = this.name().trim();
    if (!name) return;
    this.saved.emit({
      name,
      parentGroupId: this.parentGroupId(),
      isDefaultSelection: this.isDefaultSelection(),
      icon: this.icon(),
    });
    this.target.set(null);
  }
}
